import {
  animate,
  getRelativeOffset,
  ImageScaleView,
  ImageScaleViewOpts,
  View,
} from '@play-co/timestep-core/ui';
import Match3Block from './Match3Block';
import { Match3 } from 'src/replicant/minigame/match3';
import { createEmitter } from 'src/lib/Emitter';
import { Immutable } from '@play-co/replicant';
import StateObserver from 'src/StateObserver';
import { Point, Rect } from '@play-co/timestep-core/math';
import { animDuration, withLoading } from 'src/lib/utils';
import loader from '@play-co/timestep-core/lib/ui/resource/loader';
import soundConfig from 'src/conf/soundConfig';
import { match3Actions } from '../match3.redux';
import { assertNever } from 'src/replicant/utils';
import sounds from 'src/lib/sounds';

const { setBoardState, setMatching } = match3Actions;

type BoardState = Immutable<Match3.BoardState>;

export enum Match3Event {
  MatchingBegin = 'matchingBegin',
  MatchingComplete = 'matchingComplete',
  BoardUpdated = 'boardUpdated',
}

export type MatchHandler = (match: {
  type: Match3.BlockType;
  count: number;
}) => Promise<boolean>;

export default class Match3Board {
  readonly view: View;
  private blocks: Match3Block[] = [];
  private dragging: Match3Block | null = null;

  onBeforeMatch?: MatchHandler;
  onMatch?: MatchHandler;

  constructor(opts: Omit<ImageScaleViewOpts, keyof typeof skin.board>) {
    void withLoading(() => this.loadAssets());

    this.view = new ImageScaleView({
      ...opts,
      ...skin.board.view,
    }).on('InputMove', () => {
      if (this.dragging) {
        this.onBlockDragged();
      }
    });

    createEmitter(this.view, ({ match3 }) => match3.board).addListener(
      (board) => {
        if (StateObserver.getState().match3.matching) {
          return;
        }

        console.log(`Resetting Match3 board state to ${JSON.stringify(board)}`);

        this.blocks.forEach((block) => this.removeBlock(block, 0));
        this.populateBoard(board);
      },
    );
  }

  private async loadAssets() {
    const urls = skin.sfx.flatMap((sound) => {
      const { sources } = soundConfig.sfx[sound];
      return sources.map((asset) => `${soundConfig.sfxPath}/${asset}`);
    });
    await loader.loadAssets(urls);
  }

  private onBlockDragged = () => {
    const a = this.dragging.getBoardPosition();
    for (let i = 0; i < this.blocks.length; ++i) {
      const block = this.blocks[i];
      const b = block.getBoardPosition();
      if (Match3.ruleset.positionsEqual(a, b)) {
        continue;
      }

      const { x, y } = getRelativeOffset(block.view, this.dragging.view);
      const { width, height } = block.view.style;
      const swapPoint = new Point(x + width / 2, y + height / 2);
      if (block.view.containsLocalPoint(swapPoint)) {
        void this.dragging.cancelDrag();
        this.dragging = null;
        void this.swapBlocks({ a, b });
        break;
      }
    }
  };

  private swapBlocks = async (swap: Match3.PositionSwap) => {
    const { positionToString } = Match3.ruleset;
    console.log(
      `Swapping ${positionToString(swap.a)} with ${positionToString(swap.b)}`,
    );
    await StateObserver.invoke.match3SwapBlocks(swap);
    const swapping = this.swapBlockComponents(swap);
    sounds.playSound('match3Move', 0.5);

    try {
      this.view.publish(Match3Event.MatchingBegin);
      StateObserver.dispatch(setMatching(true));
      await this.matchBlocks(swapping);
      await StateObserver.invoke.match3EndTurn();
    } finally {
      StateObserver.dispatch(setMatching(false));
      this.view.publish(Match3Event.MatchingComplete);
    }
  };

  private swapBlockComponents = (swapping: Match3.PositionSwap) => {
    let a: Match3Block, b: Match3Block;

    const { positionsEqual } = Match3.ruleset;
    for (let i = 0; i < this.blocks.length && (!a || !b); ++i) {
      const block = this.blocks[i];
      const position = block.getBoardPosition();
      if (positionsEqual(position, swapping.a)) {
        a = block;
      } else if (positionsEqual(position, swapping.b)) {
        b = block;
      }
    }

    a.setBoardPosition(swapping.b);
    b.setBoardPosition(swapping.a);
    return Promise.all([
      a.moveToPoint(b.view.style),
      b.moveToPoint(a.view.style),
    ]);
  };

  private matchBlocks = async (previous?: Promise<unknown>) => {
    const matches = await StateObserver.invoke.match3ReplaceMatches();
    let matchCount = matches.length;
    if (matchCount > 0) {
      console.log(
        `Matches found: ${JSON.stringify(
          matches.map((positions) =>
            positions.map(Match3.ruleset.positionToString),
          ),
        )}`,
      );

      // Create local cache of block elements for quick lookup
      const board: Match3Block[][] = [];
      this.blocks.forEach((block) => {
        const { colIndex, rowIndex } = block.getBoardPosition();
        if (!board[colIndex]) {
          board[colIndex] = [];
        }
        board[colIndex][rowIndex] = block;
      });

      // Iterate over matches, waiting for each to be handled
      await matches.reduce(async (anim, positions) => {
        await anim;

        const { user } = StateObserver.getState();
        const currentMatch = Match3.getters(user).getCurrentMatch();
        await this.onBeforeMatch?.(currentMatch);
        await Promise.all([
          this.handleMatch(currentMatch),
          this.removeBlocks(positions),
        ]);

        // Mark blocks as removed in cache
        positions.forEach(({ colIndex, rowIndex }) => {
          board[colIndex][rowIndex] = null;
        });
      }, previous);

      const { getBoardState } = Match3.getters(StateObserver.getState().user);
      const newState = getBoardState();
      board.forEach((column, colIndex) => {
        for (let rowIndex = 0; rowIndex < column.length; ++rowIndex) {
          const block = column[rowIndex];
          if (block === null) {
            // start searching for the first non-null
            for (let row2 = rowIndex + 1; row2 < column.length; ++row2) {
              if (column[row2] !== null) {
                // bring it down (i.e. replace it with the null we found)
                column[rowIndex] = column[row2];
                column[row2] = null;

                // assign new position
                column[rowIndex].setBoardPosition({ colIndex, rowIndex });
                break;
              }
            }
          }
        }

        // Add replacement block components
        const replacement = column.indexOf(null);
        if (replacement >= 0) {
          for (let i = replacement; i < column.length; ++i) {
            // Set initial position above the board
            const rowIndex = column.length + i - replacement;
            column[i] = this.addBlock(newState[colIndex][i], {
              colIndex,
              rowIndex,
            });
            // Update position reference to desired position
            column[i].setBoardPosition({ colIndex, rowIndex: i });
          }
        }
      });

      // Animate all blocks to their updated board positions
      const animations = board.flat().map((block) => {
        return block.moveToPoint(this.getBounds(block.getBoardPosition()));
      });

      // Update redux state before searching again
      StateObserver.dispatch(setBoardState(newState));

      // Search board again for new matches
      matchCount += await this.matchBlocks(Promise.all(animations));
    }

    return matchCount;
  };

  private removeBlocks = (positions: Match3.BoardPosition[]) => {
    const animations: Promise<void>[] = [];
    this.blocks.forEach((block) => {
      const position = block.getBoardPosition();
      const shouldRemove = positions.some((pos) =>
        Match3.ruleset.positionsEqual(position, pos),
      );
      if (shouldRemove) {
        animations.push(this.removeBlock(block));
      }
    });
    return Promise.all(animations);
  };

  private getBounds = ({ colIndex, rowIndex }: Match3.BoardPosition) => {
    const { padding } = skin.board;
    const { style } = this.view;
    const containerWidth = style.width - padding.left - padding.right;
    const containerHeight = style.height - padding.top - padding.bottom;

    const { rows, columns } = Match3.ruleset;
    const width = containerWidth / columns;
    const height = containerHeight / rows;
    const x = padding.left + containerWidth - width * (colIndex + 1);
    const y = padding.top + containerHeight - height * (rowIndex + 1);

    return new Rect(x, y, width, height);
  };

  private populateBoard = (board: BoardState) => {
    board.forEach((column, colIndex) => {
      column.forEach((type, rowIndex) => {
        this.addBlock(type, { colIndex, rowIndex });
      });
    });
    this.view.publish(Match3Event.BoardUpdated);
  };

  private addBlock = (
    type: Match3.BlockType,
    position: Match3.BoardPosition,
  ) => {
    const block = new Match3Block(
      type,
      position,
      () => {
        const {
          match3: { matching, playing },
        } = StateObserver.getState();

        return !matching && playing;
      },
      {
        superview: this.view,
        ...this.getBounds(position),
      },
    );
    this.blocks.push(block);

    const { view } = block;
    view.on('DragStart', () => void (this.dragging = block));
    view.on('DragStop', () => void (this.dragging = null));

    return block;
  };

  private removeBlock = async (block: Match3Block, animDuration = 1250) => {
    const { view } = block;
    block.playAnimation('remove', animDuration);
    await animate(view)
      .wait(animDuration * 0.75) // Cut dead frames from animation
      .then(() => {
        view.removeAllListeners('DragStart');
        view.removeAllListeners('DragStop');
        view.removeFromSuperview();

        const blockIndex = this.blocks.indexOf(block);
        if (blockIndex >= 0) {
          this.blocks.splice(blockIndex, 1);
        }
      })
      .toPromise();
  };

  private handleMatch = async (match: {
    type: Match3.BlockType;
    count: number;
  }) => {
    if (await this.onMatch?.(match)) {
      return;
    }

    const {
      rewardType,
    } = await StateObserver.invoke.match3ConsumeMatchReward();

    switch (rewardType) {
      case 'points':
        sounds.sfx.play('match3Pop');
        break;

      case 'gems':
        sounds.sfx.play('match3Gems');
        break;

      default:
        assertNever(rewardType);
    }
  };
}

const skin = {
  board: {
    view: {
      image: 'assets/minigame/match3/gameboard.png',
      clip: true,
    },
    padding: {
      top: 12,
      right: 14,
      bottom: 24,
      left: 14,
    },
  },
  sfx: ['match3Move', 'match3Pop', 'match3Gems'],
} as const;
